import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Search } from 'lucide-react';
import axios from 'axios';
import { constants } from '../../../../configs/constants';
import ModelCard from './ModelCard';

export default function ModelCatalog({ selectedModel, onSelectModel, onShowDetails }) {
  const [models, setModels] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFamily, setActiveFamily] = useState('all');
  const token = useSelector(state => state.auth.token);

  useEffect(() => {
    fetchModels();
  }, [activeFamily, search]);

  async function fetchModels() {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (activeFamily !== 'all') params.family = activeFamily;
      if (search) params.search = search;

      const LLM_BASE = constants.API_URL.replace('/api', '');
      const res = await axios.get(`${LLM_BASE}/llm/v1/models`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setModels(res.data.models || []);
      if (res.data.families) setFamilies(res.data.families);
    } catch (err) {
      console.error('Failed to fetch models:', err);
    } finally {
      setLoading(false);
    }
  }

  const chatModels = models.filter(m => m.modelType !== 'embedding');
  const embeddingModels = models.filter(m => m.modelType === 'embedding');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: 16, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: '#F1F5F9', margin: '0 0 12px 0' }}>Models</h2>
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <Search size={14} color="#64748B" style={{ position: 'absolute', left: 10, top: 9 }} />
          <input
            type="text"
            placeholder="Search models..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '7px 12px 7px 32px',
              fontSize: 12, background: '#111', color: '#F1F5F9', borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.1)', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {['all', ...families].map(f => (
            <button
              key={f}
              onClick={() => setActiveFamily(f)}
              style={{
                padding: '5px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer',
                border: 'none', textTransform: 'capitalize',
                transition: 'all 0.15s',
                background: activeFamily === f ? '#7C3AED' : 'transparent',
                color: activeFamily === f ? '#fff' : '#94A3B8',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: '#64748B', fontSize: 13 }}>
            Loading models...
          </div>
        ) : models.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: '#64748B', fontSize: 13 }}>
            No models found
          </div>
        ) : (
          <>
            {chatModels.map(model => (
              <ModelCard
                key={model.name}
                model={model}
                isSelected={selectedModel === model.name}
                onSelect={onSelectModel}
                onShowDetails={onShowDetails}
              />
            ))}
            {embeddingModels.length > 0 && (
              <>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: '#64748B', padding: '12px 0 4px 0',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 4,
                }}>
                  Embedding Models
                </div>
                {embeddingModels.map(model => (
                  <ModelCard
                    key={model.name}
                    model={model}
                    isSelected={false}
                    onSelect={() => {}}
                    onShowDetails={onShowDetails}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
